import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgOnChange } from '@cikrf/gas-utils/decorators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { GasUploaderDirective } from '../../utils/gas-uploader-directive/gas-uploader.directive';
import { GasUploader } from './gas-uploader.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

//TODO: добавить описание в сторибуке + добавить тесты задача UIKIT-96

/** @see https://www.figma.com/file/YDxRp3ZZaGFI4UoB6mCcBr/GAS20_Administration_Vote?node-id=246%3A18384 */

const PROGRESS = {
  full: 100,
  half: 50,
  empty: 0,
};

const DEFAULT_PROGRESS_TITLE = 'Загрузка файла на сервер...';

@UntilDestroy()
@Component({
  selector: 'gas-uploader',
  templateUrl: './gas-uploader.component.html',
  styleUrls: ['./gas-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GasUploaderDirective],
})
export class GasUploaderComponent implements OnInit {
  /** футер для загрузки голосований */
  @ViewChild('uploadFooter', {static: true})
  public customFooterRef: ElementRef;

  /** футер при успешной загрузке */
  @ViewChild('launchFooter', {static: true})
  public launchFooterRef: ElementRef;

  /** возвращает информацию о прогрессе загрузки (количество процентов) */
  @Output('onLocalProgress')
  public onLocalProgress$ = new EventEmitter<number>();

  /** клик по launchFooterRef */
  @Output('onLaunchClick')
  public onLaunchClick$ = new EventEmitter<number>();

  /** возвращает текущий статус загрузки */
  @Output('onStatusChanged')
  public onStatusChanged$ = new EventEmitter<GasUploader.Status>();

  @NgOnChange()
  @Input('progressTitle')
  public progressTitle$: Observable<string>;

  @NgOnChange()
  @Input('uploadStatus')
  public uploadStatus$: Observable<GasUploader.Status>;

  @NgOnChange()
  @Input('uploadProgress')
  public uploadProgress$: Observable<number>;

  @NgOnChange()
  @Input('uploadError')
  public uploadError$: Observable<boolean>;

  @NgOnChange()
  @Input('uploadRepeat')
  public uploadRepeat$: Observable<void>;

  /**
   * @deprecated Использьуйте input-параметр uploadProgress.
   * @example <gas-uploader [uploadProgress]="uploadProgress$ | async"></gas-uploader>
   */
  @Input('progress')
  public set serverProgress(progressSubject$: BehaviorSubject<number>) {
    this.uploadProgress$ = progressSubject$.asObservable();
  }

  /**
   * @deprecated Использьуйте input-параметр uploadError.
   * @example <gas-uploader [uploadError]="uploadError$ | async"></gas-uploader>
   */
  @Input('error')
  public set isErrorProgress(errorSubject$: BehaviorSubject<boolean>) {
    this.uploadError$ = errorSubject$.asObservable();
  }

  /**
   * @deprecated Использьуйте input-параметр uploadRepeat.
   * @example <gas-uploader [uploadRepeat]="uploadRepeat$ | async"></gas-uploader>
   */
  @Input('repeat')
  public set repeatUploadSubject(repeatSubject$: Subject<void>) {
    this.uploadRepeat$ = repeatSubject$.asObservable();
  }

  /**
   * Требуется указывать MIME тип
   */
  @Input()
  public accept = '';

  @Input()
  public multi = false;

  @Input()
  public maxFileSize: number;

  @Input()
  public type = 'all';

  public progress$: Observable<number>;
  public error$ = new BehaviorSubject<boolean>(false);
  public title$ = new BehaviorSubject<string>(DEFAULT_PROGRESS_TITLE);
  public status$ = new BehaviorSubject<GasUploader.Status>(GasUploader.Status.Default);
  public errorSize$ = this.gasUploaderDirective.errorSubject$.asObservable();

  //eslint-disable-next-line
  public ProgressState = GasUploader.Status;

  constructor(
    private gasUploaderDirective: GasUploaderDirective,
  ) {
    this.status$
      .pipe(untilDestroyed(this))
      .subscribe((status: GasUploader.Status) => this.onStatusChanged$.emit(status));
  }

  public ngOnInit(): void {
    this.gasUploaderDirective.multi = this.multi;
    this.gasUploaderDirective.accept = this.accept;
    this.gasUploaderDirective.maxFileSize = this.maxFileSize;
    this.gasUploaderDirective.type = this.type;
    this.gasUploaderDirective.ngOnInit();

    this.progress$ = this.uploadProgress$.pipe(
      filter((progress: number) => this.isProgress(progress)),
      /** Сделано для поддержки проектов, использующих предыдущую реализацию данного компонента */
      tap((progress: number) => this.onLocalProgress$.emit(progress)),
    );

    this.uploadError$
      .pipe(
        untilDestroyed(this),
        filter((isError: boolean) => isError),
      )
      .subscribe(() => this.status$.next(GasUploader.Status.Error));

    this.uploadRepeat$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.error$.next(false);
        this.status$.next(GasUploader.Status.Default);
      });

    this.uploadStatus$
      .pipe(untilDestroyed(this))
      .subscribe((status: GasUploader.Status) => this.status$.next(status));

    this.progressTitle$
      .pipe(
        untilDestroyed(this),
        filter((title: string) => !!title),
      )
      .subscribe((title: string) => this.title$.next(title));
  }

  public uploadFile(): void {
    this.gasUploaderDirective.selectFile();
  }

  public launchHandleClick(): void {
    this.onLaunchClick$.emit();
  }

  public isLoading(progress: number): boolean {
    return progress && progress < PROGRESS.full;
  }

  public isProgressState(status: GasUploader.Status): boolean {
    return status === GasUploader.Status.InProgress;
  }

  public getFooterTemplate(status: GasUploader.Status): ElementRef | null {
    switch(status) {
      case GasUploader.Status.Default:
        return this.customFooterRef;
      case GasUploader.Status.Success:
        return this.launchFooterRef;
      default: return null;
    }
  }

  private isProgress(progress: number): boolean {
    return !!progress
      && progress <= PROGRESS.full
      && !this.error$.value;
  }
}
